import { AuthContainer, type AuthFlow } from "@/components/auth/auth-container";
import { AuthPageSkeleton } from "@/components/loading";
import { useSession } from "@/hooks/use-session";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useBrandStore } from "@/state/store/brand";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";

type AuthSearch = {
  flow?: "login" | "signup";
  reset?: boolean;
};

export const Route = createFileRoute("/auth")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): AuthSearch => ({
    flow: search.flow === "signup" || search.flow === "login" ? search.flow : undefined,
    reset: search.reset === true || search.reset === "true",
  }),
});

function RouteComponent() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const { sessionData, isLoading } = useSession();
  const selectedBrandId = useBrandStore((state) => state.selectedBrandId);
  const searchParams = useSearch({ from: "/auth" });
  const [initialFlow, setInitialFlow] = useState<AuthFlow>(searchParams.flow ?? "login");

  const { data: selectedBrand } = useQuery(
    trpc.brands.getBrand.queryOptions(
      { id: selectedBrandId! },
      {
        enabled: !!sessionData && !!selectedBrandId,
      },
    ),
  );

  useEffect(() => {
    if (searchParams.reset && sessionData) {
      setInitialFlow("updatePassword");
      return;
    }

    setInitialFlow(searchParams.flow ?? "login");
  }, [searchParams.flow, searchParams.reset, sessionData]);

  useEffect(() => {
    if (sessionData && !searchParams.reset) {
      if (selectedBrand) {
        navigate({ to: "/$brand/dashboard", params: { brand: selectedBrand?.slug ?? "" } });
      } else {
        navigate({ to: "/" });
      }
    }
  }, [
    navigate,
    searchParams.reset,
    selectedBrand,
    sessionData,
  ]);

  if (isLoading) {
    return <AuthPageSkeleton />;
  }

  return (
    <div className="min-h-svh flex">
      {/* Left: Branded panel */}
      <div className="relative hidden flex-col overflow-hidden bg-brand-ink lg:flex lg:w-1/2">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink-deep to-brand-ink" />

        {/* Blurred orbs */}
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-brand-accent/10 blur-[90px]" />
        <div className="absolute top-1/4 -right-16 h-72 w-72 rounded-full bg-brand-teal/18 blur-[80px]" />
        <div className="absolute -top-16 left-1/3 h-56 w-56 rounded-full bg-brand-accent/6 blur-[100px]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-10 xl:p-14">
          {/* Logo */}
          <div>
            <img
              src="/brand/logo/upsearch-logo-transparent-bg.png"
              alt="Upsearch"
              className="h-7"
            />
          </div>

          {/* Center content */}
          <div className="flex-1 flex flex-col justify-center gap-8">
            {/* Badge */}
            <div className="inline-flex w-fit items-center gap-2 bg-white/8 border border-white/10 rounded-full px-3.5 py-1.5">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
              <span
                className="text-[0.6875rem] font-bold uppercase tracking-[0.15em] text-brand-accent"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                AI Brand Monitoring
              </span>
            </div>

            {/* Heading */}
            <h2
              className="text-[2.25rem] xl:text-[2.75rem] font-black text-white leading-[1.08] tracking-[-0.03em] max-w-sm"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Get Your Brand Recommended by AI
            </h2>

            {/* Description */}
            <p
              className="text-white/55 text-[0.9375rem] leading-[1.6] max-w-xs"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Monitor and optimize your brand's visibility across ChatGPT, Claude, Gemini, and more.
            </p>

            {/* Feature list */}
            <div className="space-y-3.5">
              {[
                "Track AI-powered brand mentions",
                "Analyze competitor visibility",
                "Generate AI optimization strategies",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-accent/25 bg-brand-accent/15">
                    <div className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
                  </div>
                  <span
                    className="text-white/60 text-sm"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div
            className="text-white/25 text-xs"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            © {new Date().getFullYear()} Upsearch. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right: Form panel */}
      <div className="flex flex-1 items-center justify-center p-6 md:p-10 bg-background">
        <div className="w-full max-w-sm">
          <AuthContainer initialFlow={initialFlow} />
        </div>
      </div>
    </div>
  );
}
